/**
 * Formats a game recap string for sharing — neutral format showing both teams.
 * @param {object} params
 * @param {string} params.date - ISO date string or formatted date
 * @param {string} params.location - optional location
 * @param {Array}  params.winners - array of { name, points, rebounds, assists, steals, blocks }
 * @param {Array}  params.losers - array of { name, points, rebounds, assists, steals, blocks }
 * @param {number} params.winnersScore - total points for winning team
 * @param {number} params.losersScore - total points for losing team
 * @returns {string}
 */
export function formatGameRecap({ date, location, winners = [], losers = [], winnersScore, losersScore }) {
  const formattedDate = date
    ? (() => {
        try {
          const d = date?.includes('/') ? new Date(date) : new Date(date + 'T00:00:00');
          return d?.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
        } catch {
          return date;
        }
      })()
    : '';

  const formatStatLine = (p) => {
    const pts = p?.points ?? 0;
    const reb = p?.rebounds ?? 0;
    const ast = p?.assists ?? 0;
    const stl = p?.steals ?? 0;
    const blk = p?.blocks ?? 0;
    return `  ${p?.name}: ${pts} PTS / ${reb} REB / ${ast} AST / ${stl} STL / ${blk} BLK`;
  };

  const lines = [
    `🏀 Basketball Game Recap`,
    formattedDate ? `📅 ${formattedDate}` : '',
    location ? `📍 ${location}` : '',
    '',
    `🏆 Winners (${winnersScore ?? winners?.reduce((s, p) => s + (p?.points ?? 0), 0)} PTS):`,
    ...winners?.map(formatStatLine),
    '',
    `😤 Losers (${losersScore ?? losers?.reduce((s, p) => s + (p?.points ?? 0), 0)} PTS):`,
    ...losers?.map(formatStatLine),
    '',
    '#Basketball #Hoops #GameRecap',
  ];

  return lines?.filter((l) => l !== null && l !== undefined)?.join('\n')?.trim();
}

/**
 * Opens the device's native SMS app with a pre-filled game recap message.
 * Uses the `sms:` URI scheme which works on iOS and Android.
 * @param {string} text - formatted recap text
 */
export function textRecap(text) {
  const encoded = encodeURIComponent(text);
  // sms:?&body= works on both iOS (?&body=) and Android (?body=)
  window.location.href = `sms:?&body=${encoded}`;
}

/**
 * Share a game recap using Web Share API, falling back to clipboard copy.
 * @param {string} text - formatted recap text
 * @param {function} onCopied - callback when clipboard fallback is used
 * @returns {Promise<'shared'|'copied'|'error'>}
 */
export async function shareRecap(text, onCopied) {
  if (navigator.share) {
    try {
      await navigator.share({
        title: '🏀 Basketball Game Recap',
        text,
      });
      return 'shared';
    } catch (err) {
      if (err?.name === 'AbortError') return 'aborted';
      // fall through to clipboard
    }
  }
  // Clipboard fallback
  try {
    await navigator.clipboard?.writeText(text);
    if (onCopied) onCopied();
    return 'copied';
  } catch {
    return 'error';
  }
}
